import {
  consultBudgetKeys,
  consultBudgets,
  purchaseMethodKeys,
  purchaseMethodLabels,
} from "@car/api/catalog-config";
import { MODEL_YEAR_MIN, modelYearMax } from "@car/api/model-year";
import { dealerJsonLd, websiteJsonLd } from "@car/api/seo";
import { Button, buttonVariants } from "@car/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@car/ui/components/dialog";
import { FormAlert, useAppForm, useStore } from "@car/ui/components/form";
import { Image } from "@car/ui/components/image";
import { NativeSelect, NativeSelectOption } from "@car/ui/components/native-select";
import { Skeleton } from "@car/ui/components/skeleton";
import {
  mobileText,
  modelYearText,
  optionalText,
  parseIntText,
  requiredText,
} from "@car/ui/lib/form-rules";
import { cn, SEP } from "@car/ui/lib/utils";
import {
  ArrowLeft02Icon,
  Call02Icon,
  CheckmarkCircle02Icon,
  PackageCheckIcon,
  PauseIcon,
  PlayIcon,
  ShieldCheckIcon,
  Wallet02Icon,
  Wrench02Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { BranchCard } from "@/components/branch-card";
import { readAttribution } from "@/lib/attribution";
import { Hero, HeroPreloader } from "@/components/hero";
import { useHomeMotion } from "@/components/home-motion";
import { Lineup } from "@/components/lineup";
import { MobileNavigation, SiteFooter } from "@/components/site-footer";
import { BannerStrip, SiteHeader, salesHref } from "@/components/site-header";
import {
  bodyKeys,
  bodyLabels,
  consultTimeKeys,
  consultTimeLabels,
  consultTopicKeys,
  consultTopicLabels,
  plateKindLabels,
  plateKinds,
  fuelKeys,
  fuelLabels,
  isBodyKey,
  loadSiteContent,
  useSiteContent,
  type BodyKey,
  type ConsultTimeKey,
  type FuelKey,
  type Guide,
  type HomeView,
  type HomepageSectionKey,
  type SiteContent,
} from "@/lib/content";
import { faDate, faNumber, toEnDigits, toFaDigits } from "@/lib/format";
import { heroFramePreloads } from "@/lib/hero-frames";
import { siteUrl } from "@/lib/seo";
import { orpc } from "@/utils/orpc";

import { publicPageHeaders } from "./-public-cache";

export const Route = createFileRoute("/")({
  // the dealership's JSON-LD (sales line, branches) for search engines; without the API
  // it lists no branch rather than failing the page
  loader: async ({ context }) => {
    const [content, branches] = await Promise.all([
      loadSiteContent(context.queryClient),
      context.queryClient
        .ensureQueryData(orpc.site.branches.queryOptions())
        .then((result) => result.branches)
        .catch(() => []),
    ]);
    return { content, branches };
  },
  head: ({ loaderData }) => ({
    links: heroFramePreloads,
    meta: [
      { "script:ld+json": websiteJsonLd(siteUrl) },
      ...(loaderData
        ? [
            {
              "script:ld+json": dealerJsonLd(siteUrl, {
                description: loaderData.content.seo.description,
                telephone: loaderData.content.contact.salesPhone,
                branches: loaderData.branches,
              }),
            },
          ]
        : []),
    ],
  }),
  headers: publicPageHeaders,
  component: HomeComponent,
});

function HomeComponent() {
  const homepage = useQuery(orpc.site.home.queryOptions());
  const data = homepage.data;
  const loading = homepage.isPending;
  const root = useRef<HTMLDivElement>(null);
  // until the sections arrive every block renders (with skeletons); then staff's switches rule
  const shows = (key: HomepageSectionKey) => !data || data.sections.includes(key);
  const bannersAt = (position: HomeView["banners"][number]["position"]) =>
    (data?.banners ?? []).filter((banner) => banner.position === position);
  const hero = shows("hero");
  const topBanner = bannersAt("home_top")[0];
  useHomeMotion(root, [data]);

  return (
    <div ref={root} className="car-home bg-background text-foreground">
      {hero ? <HeroPreloader /> : null}
      <SiteHeader overlay={hero} />
      <div id="smooth-wrapper">
        <div id="smooth-content">
          <main className={cn("min-h-[100dvh] pb-20 md:pb-0", !hero && "pt-16 lg:pt-[72px]")}>
            {hero ? (
              <Hero
                note={
                  topBanner ? { title: topBanner.title, href: topBanner.href || "#" } : undefined
                }
              />
            ) : null}
            <VehicleShowcase data={data} loading={loading} finder={shows("finder")} />
            <ConsultSection />
            {/* {shows("finance") ? <ConversionTools data={data} /> : null} */}
            <TrustSection />
            {bannersAt("home_middle").length > 0 ? (
              <div className="home-container pt-10">
                <BannerStrip banners={bannersAt("home_middle")} />
              </div>
            ) : null}
            {shows("reviews") ? <Reviews data={data} loading={loading} /> : null}
            {shows("guides") ? <Guides data={data} loading={loading} /> : null}
            {shows("branches") ? <Branches data={data} loading={loading} /> : null}
            <SiteFooter />
          </main>
        </div>
      </div>
      <MobileNavigation />
    </div>
  );
}

/** سه قدم: شناختن، مقایسه، مشاوره؛ شماره‌ها ترتیبِ واقعی مسیر را می‌گویند و متن‌ها از مدیریت محتواست */
const consultSteps = (text: SiteContent["homeShowcase"]) =>
  [
    { num: "۱", title: text.step1Title, text: text.step1Text, to: "/vehicles" },
    { num: "۲", title: text.step2Title, text: text.step2Text, to: "/compare" },
    { num: "۳", title: text.step3Title, text: text.step3Text, to: null },
  ] as const;

function ConsultSection() {
  const { homeShowcase: text } = useSiteContent();
  return (
    <section id="consult" className="dark home-stage scroll-mt-28">
      <div className="home-container home-stage-grid">
        <div>
          <div className="home-head">
            <h2 className="home-title" data-split>
              {text.stepsTitle}
            </h2>
            <p className="home-lede" data-reveal>
              {text.stepsLede}
            </p>
          </div>
          <ol className="home-steps" data-reveal-group>
            {consultSteps(text).map((step) => {
              const body = (
                <>
                  <span className="home-step-num" aria-hidden="true">
                    {step.num}
                  </span>
                  <span>
                    <span className="home-step-title">{step.title}</span>
                    <span className="home-step-text">{step.text}</span>
                  </span>
                  <HugeiconsIcon
                    icon={ArrowLeft02Icon}
                    strokeWidth={1.8}
                    className="size-5 text-brand-yellow"
                  />
                </>
              );
              return (
                <li key={step.num}>
                  {step.to ? (
                    <Link to={step.to} className="home-step">
                      {body}
                    </Link>
                  ) : (
                    <a href="#consult-form" className="home-step">
                      {body}
                    </a>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        <div data-reveal="0.1">
          <ConsultForm />
        </div>
      </div>
    </section>
  );
}

const consultSchema = z
  .object({
    name: requiredText("نام و نام خانوادگی", { max: 60 }),
    phone: mobileText(),
    topic: z.enum(consultTopicKeys),
    time: z.enum(consultTimeKeys),
    // اختیاری: کارشناس با همین‌ها آماده‌تر تماس می‌گیرد
    budget: z.string(),
    purchaseMethod: z.string(),
    city: optionalText("شهر", 40),
    note: optionalText("توضیح", 500),
    // فقط برای «سفارش خودروی خارج از کاتالوگ»
    brand: optionalText("برند", 40),
    model: optionalText("مدل", 60),
    trim: optionalText("تیپ", 60),
    modelYear: modelYearText("سال مدل", {
      min: MODEL_YEAR_MIN,
      max: modelYearMax(),
      optional: true,
    }),
    color: optionalText("رنگ", 40),
    plateKind: z.string(),
  })
  .superRefine((values, context) => {
    if (values.topic !== "custom") return;
    if (values.brand.trim().length < 2) {
      context.addIssue({ code: "custom", path: ["brand"], message: "برند خودرو را بنویسید" });
    }
    if (values.model.trim() === "") {
      context.addIssue({ code: "custom", path: ["model"], message: "مدل خودرو را بنویسید" });
    }
  });

const consultDefaults: z.input<typeof consultSchema> = {
  name: "",
  phone: "",
  topic: "buy",
  time: "any",
  budget: "",
  purchaseMethod: "",
  city: "",
  note: "",
  brand: "",
  model: "",
  trim: "",
  modelYear: "",
  color: "",
  plateKind: "",
};

const plateKindOptions = plateKinds.map((kind) => ({ value: kind, label: plateKindLabels[kind] }));

const toOptions = <Key extends string>(keys: readonly Key[], labels: Record<Key, string>) =>
  keys.map((key) => ({ value: key, label: labels[key] }));

const consultTopicOptions = toOptions(consultTopicKeys, consultTopicLabels);
const consultTimeOptions = toOptions(consultTimeKeys, consultTimeLabels);
const consultBudgetOptions = consultBudgetKeys.map((key) => ({
  value: key,
  label: consultBudgets[key].label,
}));
const purchaseMethodOptions = toOptions(purchaseMethodKeys, purchaseMethodLabels);

/** برچسب‌های فرم‌های صفحه اصلی مثل قبل پررنگ می‌مانند */
const boldLabels = "[&_[data-slot=field-label]]:font-bold";

type ConsultSent = { name: string; phone: string; topic: string; time: ConsultTimeKey };

function ConsultForm() {
  const { homeShowcase: text } = useSiteContent();
  const [error, setError] = useState("");
  // پس از ثبت، به‌جای فرم کارت تأیید ماندگار (نه toast چندثانیه‌ای) تا «ثبت درخواست دیگر»
  const [sent, setSent] = useState<ConsultSent | null>(null);

  const mutation = useMutation(
    orpc.site.requestConsult.mutationOptions({
      onSuccess: (result, input) => {
        if (!result.ok) {
          setError(result.reason);
          return;
        }
        setSent({ name: input.name, phone: input.phone, topic: result.topic, time: input.time });
      },
      onError: () => setError("ثبت درخواست انجام نشد؛ لطفاً دوباره تلاش کنید."),
    }),
  );

  const form = useAppForm({
    defaultValues: consultDefaults,
    validators: { onBlur: consultSchema },
    onSubmit: async ({ value, formApi }) => {
      setError("");
      const request = {
        name: value.name.trim(),
        phone: toEnDigits(value.phone.trim()),
        topic: value.topic,
        time: value.time,
        note: value.note.trim(),
        budget: consultBudgetKeys.find((key) => key === value.budget),
        purchaseMethod: purchaseMethodKeys.find((key) => key === value.purchaseMethod),
        city: value.city.trim(),
        attribution: readAttribution(),
      };
      // the special-order fields go only with that topic
      const custom = {
        ...request,
        custom: {
          brand: value.brand.trim(),
          model: value.model.trim(),
          trim: value.trim.trim(),
          modelYear: parseIntText(value.modelYear),
        },
        color: value.color.trim(),
        plateKind: plateKinds.find((kind) => kind === value.plateKind),
      };
      const result = await mutation
        .mutateAsync(value.topic === "custom" ? custom : request)
        .catch(() => undefined);
      // نام، شماره و توضیح پاک می‌شوند؛ موضوع و زمان تماس برای درخواست بعدی می‌مانند
      if (result?.ok) {
        formApi.reset(
          { ...consultDefaults, topic: value.topic, time: value.time },
          { keepDefaultValues: true },
        );
      }
    },
  });

  const custom = useStore(form.store, (state) => state.values.topic === "custom");

  if (sent) return <ConsultSuccess sent={sent} onAgain={() => setSent(null)} />;

  return (
    <form.AppForm>
      <form.Form
        id="consult-form"
        className="home-form scroll-mt-28"
        aria-labelledby="consult-title"
      >
        <h3 id="consult-title" className="text-2xl font-black">
          {text.consultTitle}
        </h3>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text.consultIntro}</p>

        <div className={cn("mt-5 grid gap-4 sm:grid-cols-2", boldLabels)}>
          <form.AppField name="name">
            {(field) => (
              <field.TextField
                label="نام و نام خانوادگی"
                autoComplete="name"
                maxLength={60}
                placeholder="مثلاً: آرمان رادمهر"
              />
            )}
          </form.AppField>
          <form.AppField name="phone">
            {(field) => (
              <field.TextField
                label="شماره موبایل"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                dir="ltr"
                className="text-start"
                maxLength={14}
                placeholder="مثلاً ۰۹۱۲۱۲۳۴۵۶۷"
              />
            )}
          </form.AppField>
          <form.AppField name="topic">
            {(field) => <field.SelectField label="موضوع مشاوره" options={consultTopicOptions} />}
          </form.AppField>
          <form.AppField name="time">
            {(field) => <field.SelectField label="زمان مناسب تماس" options={consultTimeOptions} />}
          </form.AppField>
          <form.AppField name="budget">
            {(field) => (
              <field.SelectField
                label="بودجه (اختیاری)"
                placeholder="انتخاب نشده"
                options={consultBudgetOptions}
              />
            )}
          </form.AppField>
          <form.AppField name="purchaseMethod">
            {(field) => (
              <field.SelectField
                label="روش خرید (اختیاری)"
                placeholder="انتخاب نشده"
                options={purchaseMethodOptions}
              />
            )}
          </form.AppField>
          <form.AppField name="city">
            {(field) => (
              <field.TextField
                label="شهر (اختیاری)"
                autoComplete="address-level2"
                maxLength={40}
                fieldClassName="sm:col-span-2"
                placeholder="مثلاً: تهران"
              />
            )}
          </form.AppField>
          {custom ? (
            <fieldset className="grid gap-4 rounded-xl border p-4 sm:col-span-2 sm:grid-cols-2">
              <legend className="px-1 text-sm font-bold">خودرویی که می‌خواهید</legend>
              <form.AppField name="brand">
                {(field) => (
                  <field.TextField label="برند" maxLength={40} placeholder="مثلاً تویوتا" />
                )}
              </form.AppField>
              <form.AppField name="model">
                {(field) => (
                  <field.TextField label="مدل" maxLength={60} placeholder="مثلاً لندکروزر" />
                )}
              </form.AppField>
              <form.AppField name="trim">
                {(field) => (
                  <field.TextField label="تیپ (اختیاری)" maxLength={60} placeholder="مثلاً GR-S" />
                )}
              </form.AppField>
              <form.AppField name="modelYear">
                {(field) => (
                  <field.TextField
                    label="سال مدل میلادی (اختیاری)"
                    inputMode="numeric"
                    dir="ltr"
                    className="text-start"
                    maxLength={4}
                    placeholder={String(new Date().getFullYear())}
                  />
                )}
              </form.AppField>
              <form.AppField name="color">
                {(field) => (
                  <field.TextField label="رنگ (اختیاری)" maxLength={40} placeholder="مثلاً مشکی" />
                )}
              </form.AppField>
              <form.AppField name="plateKind">
                {(field) => (
                  <field.SelectField
                    label="نوع پلاک"
                    placeholder="فرقی ندارد"
                    options={plateKindOptions}
                  />
                )}
              </form.AppField>
            </fieldset>
          ) : null}
          <form.AppField name="note">
            {(field) => (
              <field.TextareaField
                label="توضیح (اختیاری)"
                fieldClassName="sm:col-span-2"
                maxLength={500}
                rows={3}
                placeholder="کاربری خودرو یا مدل‌هایی که در نظر دارید."
              />
            )}
          </form.AppField>
        </div>

        <FormAlert message={error} className="mt-3" />

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <form.SubmitButton className="bg-brand-yellow hover:bg-brand-yellow/90" size="lg">
            ثبت درخواست مشاوره
          </form.SubmitButton>
          <span className="text-xs text-muted-foreground">
            خدمات مالکیت و پیگیری درخواست‌ها در{" "}
            <a href="/panel" className="font-bold underline underline-offset-4">
              پنل مشتریان
            </a>{" "}
            انجام می‌شود.
          </span>
        </div>
      </form.Form>
    </form.AppForm>
  );
}

/** تأیید ماندگار درخواست مشاوره: چه ثبت شد، کی و با چه شماره‌ای تماس می‌گیرند و قدم بعدی */
function ConsultSuccess({ sent, onAgain }: { sent: ConsultSent; onAgain: () => void }) {
  const { contact } = useSiteContent();
  const title = useRef<HTMLHeadingElement>(null);
  // تمرکز روی عنوان تأیید تا صفحه‌خوان و صفحه‌کلید جای فرمِ رفته را گم نکنند
  useEffect(() => title.current?.focus(), []);
  const when =
    sent.time === "any"
      ? `در اولین فرصت در ساعت کاری فروش (${contact.salesHours})`
      : `در بازه‌ای که انتخاب کردید، ${consultTimeLabels[sent.time]}`;

  return (
    <div id="consult-form" className="home-form scroll-mt-28" role="status">
      <h3
        ref={title}
        tabIndex={-1}
        className="flex items-center gap-2 text-2xl font-black outline-none"
      >
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          strokeWidth={1.8}
          className="size-7 shrink-0 text-success"
        />
        درخواست شما ثبت شد
      </h3>
      <p className="mt-3 leading-7">
        {sent.name} عزیز، کارشناس فروش درباره‌ی «{sent.topic}» {when} با شماره{" "}
        <span dir="ltr" className="font-bold">
          {toFaDigits(sent.phone)}
        </span>{" "}
        تماس می‌گیرد.
      </p>
      <ul className="mt-3 grid list-disc gap-1.5 ps-5 text-sm leading-6 text-muted-foreground">
        <li>نیازی به ثبت دوباره نیست؛ درخواست تازه با همین شماره به همان پرونده اضافه می‌شود.</li>
        <li>اگر شماره اشتباه است، درخواست دیگری با شماره درست ثبت کنید.</li>
      </ul>
      <div className="mt-5 flex flex-wrap gap-3">
        <a href={salesHref(contact.salesPhone)} className={buttonVariants({ size: "lg" })}>
          <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} />
          {contact.salesPhone ? "عجله دارید؟ تماس با فروش" : "تلفن شعبه‌ها"}
        </a>
        <Button variant="outline" size="lg" onClick={onAgain}>
          ثبت درخواست دیگر
        </Button>
      </div>
    </div>
  );
}

function VehicleShowcase({
  data,
  loading,
  finder,
}: {
  data?: HomeView;
  loading: boolean;
  finder: boolean;
}) {
  const { homeShowcase: text } = useSiteContent();
  return (
    <section id="inventory" className="home-section scroll-mt-28">
      <div className="home-container">
        <div className="home-head">
          <h2 className="home-title" data-split>
            {text.showcaseTitle}
          </h2>
          <p className="home-lede" data-reveal>
            {text.showcaseLede}
          </p>
        </div>
        {finder ? <VehicleFinder /> : null}
        <div className="mt-10" data-reveal="0.1">
          <Lineup vehicles={data?.vehicles ?? []} loading={loading} />
        </div>
        <div className="home-more" data-reveal>
          <Link to="/vehicles" className="home-link">
            {text.showcaseLink}
            <HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={1.8} className="size-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/** جستجوگر: شمارش زنده مدل‌های مطابق سوخت و بدنه، با لینک به همان فیلتر در /vehicles */
function VehicleFinder() {
  const [fuel, setFuel] = useState<FuelKey | undefined>(undefined);
  const [body, setBody] = useState<BodyKey | undefined>(undefined);
  const result = useQuery({
    ...orpc.site.findVehicles.queryOptions({ input: { fuel, body } }),
    placeholderData: (previous) => previous,
  });
  const count = result.data?.count;

  return (
    <div className="home-finder" data-reveal>
      <label className="grid gap-2 text-sm font-bold">
        نوع سوخت
        {/* form-kit-exempt: فیلتر زنده جستجو، فرمی ارسال نمی‌شود */}
        <NativeSelect
          className="w-full"
          value={fuel ?? "all"}
          onChange={(event) =>
            setFuel(fuelKeys.find((key) => key === event.target.value) ?? undefined)
          }
        >
          <NativeSelectOption value="all">همه</NativeSelectOption>
          {fuelKeys.map((key) => (
            <NativeSelectOption key={key} value={key}>
              {fuelLabels[key]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </label>
      <label className="grid gap-2 text-sm font-bold">
        نوع بدنه
        {/* form-kit-exempt: فیلتر زنده جستجو، فرمی ارسال نمی‌شود */}
        <NativeSelect
          className="w-full"
          value={body ?? "all"}
          onChange={(event) =>
            setBody(isBodyKey(event.target.value) ? event.target.value : undefined)
          }
        >
          <NativeSelectOption value="all">همه</NativeSelectOption>
          {bodyKeys.map((key) => (
            <NativeSelectOption key={key} value={key}>
              {bodyLabels[key]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </label>
      <a
        href={result.data?.href ?? "/vehicles"}
        className={cn(buttonVariants({ size: "lg" }), "h-11")}
        aria-live="polite"
      >
        {count === undefined ? "دیدن خودروها" : `دیدن ${faNumber(count)} خودرو`}
      </a>
    </div>
  );
}

/** نشان‌های نوار اعتماد: آیکن هر جایگاه ثابت است و متن‌ها از مدیریت محتوا */
const trustItems = (text: SiteContent["homeTools"]) =>
  [
    [ShieldCheckIcon, text.trust1Title, text.trust1Detail],
    [Wrench02Icon, text.trust2Title, text.trust2Detail],
    [PackageCheckIcon, text.trust3Title, text.trust3Detail],
    [Wallet02Icon, text.trust4Title, text.trust4Detail],
  ] as const;

const MARQUEE_STILL_KEY = "home-marquee-still";

/** نوار اعتماد ایستاده است یا نه؛ انتخاب بازدیدکننده در حافظه‌ی مرورگر می‌ماند (پس از هیدریشن خوانده می‌شود) */
function useMarqueeStill() {
  const [still, setStill] = useState(false);
  useEffect(() => {
    try {
      setStill(localStorage.getItem(MARQUEE_STILL_KEY) === "1");
    } catch {
      // بدون حافظه‌ی مرورگر نوار با حرکت شروع می‌شود
    }
  }, []);
  const toggle = () => {
    setStill(!still);
    try {
      localStorage.setItem(MARQUEE_STILL_KEY, still ? "0" : "1");
    } catch {
      // بدون حافظه‌ی مرورگر فقط در همین بازدید می‌ماند
    }
  };
  return [still, toggle] as const;
}

function TrustSection() {
  const { homeTools: text } = useSiteContent();
  // WCAG 2.2.2: حرکت خودکارِ بیش از پنج ثانیه دکمه‌ی توقف می‌خواهد؛ ایستاده، همه‌ی نشان‌ها ثابت و کامل دیده می‌شوند
  const [still, toggleStill] = useMarqueeStill();
  return (
    <section className="home-trust bg-primary text-primary-foreground">
      <div className="home-container">
        <h2 className="home-title" data-split>
          {text.trustTitle}
        </h2>
        <Button
          variant="secondary"
          size="lg"
          className="home-marquee-toggle mt-6 focus-visible:ring-primary-foreground"
          onClick={toggleStill}
        >
          <HugeiconsIcon icon={still ? PlayIcon : PauseIcon} strokeWidth={1.8} />
          {still ? "حرکت دوباره‌ی نوار" : "توقف حرکت نوار"}
        </Button>
      </div>
      <div className="home-marquee" data-reveal data-still={still || undefined}>
        <ul className="home-marquee-track">
          {[false, true].map((duplicate) =>
            trustItems(text).map(([icon, title, detail], index) => (
              <li key={`${index}-${duplicate}`} aria-hidden={duplicate || undefined}>
                <HugeiconsIcon icon={icon} strokeWidth={1.6} className="size-8 shrink-0" />
                <span>{title}</span>
                <span className="home-marquee-detail">{detail}</span>
              </li>
            )),
          )}
        </ul>
      </div>
    </section>
  );
}

function Reviews({ data, loading }: { data?: HomeView; loading: boolean }) {
  const { homeStories: text } = useSiteContent();
  if (!loading && data?.reviews.length === 0) return null;
  return (
    <section className="home-section">
      <div className="home-container">
        <div className="home-head">
          <h2 className="home-title" data-split>
            {text.reviewsTitle}
          </h2>
          <p className="home-lede" data-reveal>
            {text.reviewsLede}
          </p>
        </div>
      </div>
      <div className="home-rail" data-reveal>
        {loading
          ? [0, 1].map((key) => <Skeleton key={key} className="home-quote h-48 rounded-2xl" />)
          : data?.reviews.map((review) => (
              <blockquote key={review.id} className="home-quote">
                <p>{review.quote}</p>
                <footer>
                  <cite className="not-italic font-black">{review.name}</cite>
                  <span className="text-sm text-muted-foreground">{review.detail}</span>
                  <span className="text-sm text-muted-foreground">{review.kind}</span>
                </footer>
              </blockquote>
            ))}
      </div>
    </section>
  );
}

function Guides({ data, loading }: { data?: HomeView; loading: boolean }) {
  const { homeStories: text } = useSiteContent();
  const [first, ...rest] = data?.guides ?? [];
  if (!loading && !first && (data?.news.length ?? 0) === 0) return null;
  return (
    <section id="guides" className="home-section scroll-mt-28">
      <div className="home-container">
        <div className="home-head">
          <h2 className="home-title" data-split>
            {text.guidesTitle}
          </h2>
          <p className="home-lede" data-reveal>
            {text.guidesLede}
          </p>
        </div>
        {loading ? (
          <Skeleton className="mt-10 h-[440px] rounded-3xl" />
        ) : (
          <>
            {first ? (
              <div className="home-guides">
                <GuideCard guide={first} featured />
                <div className="guide-rows" data-reveal-group>
                  {rest.map((guide) => (
                    <GuideCard key={guide.id} guide={guide} />
                  ))}
                </div>
              </div>
            ) : null}
            {data && data.news.length > 0 ? (
              <div className="home-news" data-reveal>
                <h3 className="text-xl font-black">{text.newsTitle}</h3>
                <ul>
                  {data.news.map((item) => (
                    <li key={item.id}>
                      <GuideReader
                        guide={item}
                        trigger={
                          <button
                            type="button"
                            className="grid text-start hover:text-primary focus-visible:outline-3 focus-visible:outline-ring"
                          />
                        }
                      >
                        <span className="text-xs text-muted-foreground">
                          {faDate(item.dateIso)}
                        </span>
                        <span className="mt-1 font-bold">{item.title}</span>
                      </GuideReader>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

/** متن کامل مطلب در یک دیالوگ؛ پاراگراف‌ها با خط خالی از هم جدا شده‌اند */
function GuideReader({
  guide,
  trigger,
  children,
}: {
  guide: Guide;
  /** دکمه بازکننده؛ محتوایش children است */
  trigger: React.ReactElement;
  children: React.ReactNode;
}) {
  return (
    <Dialog>
      <DialogTrigger render={trigger}>{children}</DialogTrigger>
      <DialogContent lang="fa" dir="rtl" className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl leading-8">{guide.title}</DialogTitle>
          <DialogDescription>
            {[guide.category, guide.readTime, faDate(guide.dateIso)].filter(Boolean).join(SEP)}
          </DialogDescription>
        </DialogHeader>
        {guide.image ? (
          <Image
            src={guide.image}
            alt=""
            width={1280}
            height={720}
            sizes="(max-width: 672px) 100vw, 672px"
            className="max-h-64 w-full rounded-xl object-cover"
          />
        ) : null}
        <div className="grid gap-4 leading-8">
          {(guide.body || guide.summary)
            .split(/\n\s*\n/)
            .filter((paragraph) => paragraph.trim() !== "")
            .map((paragraph, index) => (
              <p key={index}>{paragraph.trim()}</p>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GuideCard({ guide, featured = false }: { guide: Guide; featured?: boolean }) {
  const read = (
    <GuideReader
      guide={guide}
      trigger={<button type="button" className="home-link mt-2 text-base" />}
    >
      خواندن راهنما
      <HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={1.8} className="size-5" />
    </GuideReader>
  );

  if (featured) {
    return (
      <article className="guide-featured">
        <div className="guide-featured-media" data-clip>
          {guide.image ? (
            <Image
              src={guide.image}
              alt=""
              width={1280}
              height={800}
              sizes="(max-width: 1024px) 100vw, 760px"
            />
          ) : null}
        </div>
        <div className="grid gap-3" data-reveal>
          <h3 className="guide-title">{guide.title}</h3>
          <div className="guide-meta">
            <span>{guide.category}</span>
            <span>{guide.readTime}</span>
          </div>
          <p className="home-lede text-base">{guide.summary}</p>
          {read}
        </div>
      </article>
    );
  }

  return (
    <article className="guide-row">
      <div className="guide-row-media">
        {guide.image ? <Image src={guide.image} alt="" width={120} height={90} /> : null}
      </div>
      <div className="grid gap-1.5">
        <h3 className="guide-title">{guide.title}</h3>
        <div className="guide-meta">
          <span>{guide.category}</span>
          <span>{guide.readTime}</span>
        </div>
        {read}
      </div>
    </article>
  );
}

function Branches({ data, loading }: { data?: HomeView; loading: boolean }) {
  const { homeStories: text } = useSiteContent();
  return (
    <section id="branches" className="home-section scroll-mt-28">
      <div className="home-container home-branches">
        <div className="home-head">
          <h2 className="home-title" data-split>
            {text.branchesTitle}
          </h2>
          <p className="home-lede" data-reveal>
            {text.branchesLede}
          </p>
          <a href="/panel/appointments/new" className="home-link" data-reveal>
            {text.branchesCta}
            <HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={1.8} className="size-5" />
          </a>
        </div>
        <div className="home-branch-list" data-reveal-group>
          {loading
            ? [0, 1].map((key) => <Skeleton key={key} className="my-6 h-40 rounded-2xl" />)
            : data?.branches.map((branch) => <BranchCard key={branch.id} branch={branch} />)}
        </div>
      </div>
    </section>
  );
}
